import React, { useEffect, useRef, useState } from 'react'
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native'
import { TopNavigationBar } from 'ui/common/top-navigation-bar'
import { colors } from 'infra/color'
import { observer } from 'mobx-react'
import { formatDistance, isSamePlace, useSafeAreaInsets } from 'infra/util'
import { SearchInput } from 'ui/discovery/search-input'
import styled from 'styled-components'
import { Body1, Body3, SectionTitle } from 'ui/common/text'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { Place, UnreportedPlace } from 'infra/type'
import { navigation } from 'navigation/global'
import { debounce } from 'lodash-es'
import { searchPlaceList } from 'api/hooks'
import { crawl3rdParty } from 'infra/crawl-3rd-party'
import { locationStore } from 'store/location'
import { Vertical16, Vertical8 } from 'infra/space'
import { rgba } from 'polished'
import { CURRENT_OS, KOREA_CENTER, OS } from 'infra/constant'

export const SearchScreen = observer(() => {
  const safeAreaInsets = useSafeAreaInsets()
  const searchInputRef: React.Ref<TextInput> = useRef(null)
  useEffect(() => {
    setTimeout(() => {
      searchInputRef.current?.focus()
    })
  }, [])
  const [knownPlaces, setKnownPlaces] = useState<Place[]>([])
  const [thirdPartyPlaces, setThirdPartyPlaces] = useState<UnreportedPlace[]>(
    [],
  )
  const [unknownPlaces, setUnknownPlaces] = useState<UnreportedPlace[]>([])
  const debouncedSearch = useRef(
    debounce((v: string) => {
      if (!v) {
        setKnownPlaces([])
        setUnknownPlaces([])
        return
      }
      searchPlaceList(v, null)
        .then((res) => {
          setKnownPlaces(res)
        })
        .catch((error) => {
          console.warn('[KnownPlaceSearch]', error)
          setKnownPlaces([])
        })
      locationStore
        .getLocation(false)
        .catch((error) => {
          if (CURRENT_OS !== OS.WEB) throw error
          console.warn('[ExternalSearch] Using fallback location', error)
          return {
            lat: String(KOREA_CENTER.latitude),
            lng: String(KOREA_CENTER.longitude),
          }
        })
        .then((res) => {
          return crawl3rdParty({
            lat: String(res.lat),
            lng: String(res.lng),
            query: v,
          })
        })
        .then((res) => {
          setThirdPartyPlaces(res)
        })
        .catch((error) => {
          console.warn('[ExternalSearch]', error)
          setThirdPartyPlaces([])
        })
    }, 500),
  ).current
  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])
  useEffect(() => {
    setUnknownPlaces(
      thirdPartyPlaces.filter((x) => {
        return knownPlaces.every((y) => !isSamePlace(x, y))
      }),
    )
  }, [knownPlaces, thirdPartyPlaces])
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.white,
        paddingTop: safeAreaInsets.top,
      }}
    >
      <TopNavigationBar title='장소 제보하기' />
      <SearchInput
        goButton
        editable
        inputRef={searchInputRef}
        onChangeText={(v) => debouncedSearch(v)}
      />
      <Vertical8 />
      <ScrollView
        style={{
          paddingBottom: safeAreaInsets.bottom,
          paddingHorizontal: 20,
        }}
      >
        {knownPlaces.length > 0 && (
          <>
            <Vertical16 />
            <SectionTitle>알려진 장소</SectionTitle>
            <Vertical8 />
            {knownPlaces.map((x) => (
              <KnownPlaceRow key={x.id} data={x} />
            ))}
            <Vertical16 />
          </>
        )}
        {unknownPlaces.length > 0 && (
          <>
            <Vertical16 />
            <SectionTitle>아직 제보되지 않은 장소</SectionTitle>
            <Vertical8 />
            {unknownPlaces.map((x, i) => (
              <UnknownPlaceRow key={`${i}-${x.name}`} data={x} />
            ))}
            <Vertical16 />
          </>
        )}
      </ScrollView>
    </View>
  )
})

const KnownPlaceRow: React.FC<{
  data: Partial<Place>
}> = ({ data }) => {
  return (
    <TouchableOpacity
      onPress={() => navigation.setRoot('HomeScreen', { placeId: data.id })}
    >
      <RowLayout
        style={{
          paddingVertical: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <PlaceNameText>{data.name}</PlaceNameText>
        <KnownPlaceRowButton disabled>
          <RowLayout style={{ alignItems: 'center' }}>
            <ButtonText>지도에서 보기</ButtonText>
          </RowLayout>
        </KnownPlaceRowButton>
      </RowLayout>
    </TouchableOpacity>
  )
}

const UnknownPlaceRow: React.FC<{
  data: UnreportedPlace
}> = ({ data }) => {
  return (
    <TouchableOpacity
      onPress={() => navigation.navigate('PlaceReportScreen', { data })}
    >
      <UnknownPlaceRowContainer>
        <ColumnLayout style={{ flex: 1 }}>
          <NameCategoryContainer>
            <PlaceNameText numberOfLines={1}>{data.name}</PlaceNameText>
            <AuxText>{data.category}</AuxText>
          </NameCategoryContainer>
          <AuxText numberOfLines={1}>{data.address}</AuxText>
          <AuxText>
            {formatDistance(locationStore.getDistance(data.coord))}
          </AuxText>
        </ColumnLayout>
        <Space />
        <UnknownPlaceRowButton disabled>
          <ButtonText>장소 제보하기</ButtonText>
        </UnknownPlaceRowButton>
      </UnknownPlaceRowContainer>
    </TouchableOpacity>
  )
}

const KnownPlaceRowButton = styled(TouchableOpacity)`
  border-radius: 999px;
  padding: 6px 12px;
  background-color: ${rgba(colors.primary, 0.5)};
`

const UnknownPlaceRowButton = styled(TouchableOpacity)`
  border-radius: 999px;
  padding: 6px 12px;
  background-color: ${colors.gray.v500};
`

const ButtonText = styled(Body3)`
  color: ${colors.white};
  font-weight: 700;
`

const UnknownPlaceRowContainer = styled(RowLayout)`
  padding: 8px 0;
  align-items: center;
`

const NameCategoryContainer = styled(RowLayout)`
  align-items: flex-end;
  margin-bottom: 4px;
  flex-wrap: wrap;
`

const PlaceNameText = styled(Body1)`
  color: ${colors.gray.v800};
  font-weight: 700;
  margin-right: 4px;
`

const Space = styled(View)`
  width: 16px;
`

const AuxText = styled(Body3)`
  color: ${colors.gray.v500};
`
